import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Copy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: '', type: 'percentage', value: '', max_discount: '', min_order_amount: '',
    expiry_date: '', usage_limit: '', per_user_limit: '1', is_active: true
  });

  useEffect(() => { fetchCoupons(); }, []);

  const fetchCoupons = async () => {
    try {
      const { data } = await axios.get(`${API}/api/coupons`, { withCredentials: true });
      setCoupons(data);
    } catch { toast.error('Failed to load coupons'); }
    finally { setLoading(false); }
  };

  const resetForm = () => setForm({ code: '', type: 'percentage', value: '', max_discount: '', min_order_amount: '', expiry_date: '', usage_limit: '', per_user_limit: '1', is_active: true });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.code || !form.value) { toast.error('Code and value are required'); return; }
    const payload = {
      ...form,
      value: parseFloat(form.value) || 0,
      max_discount: parseFloat(form.max_discount) || 0,
      min_order_amount: parseFloat(form.min_order_amount) || 0,
      usage_limit: parseInt(form.usage_limit) || 0,
      per_user_limit: parseInt(form.per_user_limit) || 1,
      product_ids: [], categories: [],
    };
    try {
      if (editing) {
        await axios.put(`${API}/api/coupons/${editing.coupon_id}`, payload, { withCredentials: true });
        toast.success('Coupon updated');
      } else {
        await axios.post(`${API}/api/coupons`, payload, { withCredentials: true });
        toast.success('Coupon created');
      }
      setDialogOpen(false);
      setEditing(null);
      resetForm();
      fetchCoupons();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save coupon'); }
  };

  const handleEdit = (coupon) => {
    setEditing(coupon);
    setForm({
      code: coupon.code, type: coupon.type, value: coupon.value.toString(),
      max_discount: (coupon.max_discount || '').toString(), min_order_amount: (coupon.min_order_amount || '').toString(),
      expiry_date: coupon.expiry_date || '', usage_limit: (coupon.usage_limit || '').toString(),
      per_user_limit: (coupon.per_user_limit || 1).toString(), is_active: coupon.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await axios.delete(`${API}/api/coupons/${couponId}`, { withCredentials: true });
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch { toast.error('Failed to delete'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-black font-heading" data-testid="admin-coupons-heading">Coupons</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditing(null); resetForm(); }} data-testid="add-coupon-button"><Plus className="mr-2 h-4 w-4" /> Create Coupon</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{editing ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSave} className="space-y-4" data-testid="coupon-form">
                <div>
                  <Label>Coupon Code</Label>
                  <Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="e.g. WELCOME20" required disabled={!!editing} data-testid="coupon-code-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Discount Type</Label>
                    <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="coupon-type-select">
                      <option value="percentage">Percentage (%)</option>
                      <option value="flat">Flat Amount (₹)</option>
                    </select>
                  </div>
                  <div>
                    <Label>Discount Value</Label>
                    <Input type="number" step="0.01" value={form.value} onChange={e => setForm({...form, value: e.target.value})} placeholder={form.type === 'percentage' ? 'e.g. 20' : 'e.g. 100'} required data-testid="coupon-value-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Max Discount Cap (₹)</Label>
                    <Input type="number" step="0.01" value={form.max_discount} onChange={e => setForm({...form, max_discount: e.target.value})} placeholder="0 = no cap" data-testid="coupon-max-discount" />
                  </div>
                  <div>
                    <Label>Min Order Amount (₹)</Label>
                    <Input type="number" step="0.01" value={form.min_order_amount} onChange={e => setForm({...form, min_order_amount: e.target.value})} placeholder="0 = none" data-testid="coupon-min-order" />
                  </div>
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Input type="datetime-local" value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})} data-testid="coupon-expiry" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Total Usage Limit</Label>
                    <Input type="number" value={form.usage_limit} onChange={e => setForm({...form, usage_limit: e.target.value})} placeholder="0 = unlimited" data-testid="coupon-usage-limit" />
                  </div>
                  <div>
                    <Label>Per-User Limit</Label>
                    <Input type="number" value={form.per_user_limit} onChange={e => setForm({...form, per_user_limit: e.target.value})} placeholder="1" data-testid="coupon-per-user-limit" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} data-testid="coupon-active-switch" />
                  <Label>Active</Label>
                </div>
                <Button type="submit" className="w-full" data-testid="coupon-submit-button">{editing ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {coupons.length === 0 ? (
          <div className="text-center py-20"><p className="text-muted-foreground">No coupons yet</p></div>
        ) : (
          <div className="space-y-3">
            {coupons.map(coupon => (
              <div key={coupon.coupon_id} className="flex flex-col sm:flex-row gap-3 p-4 rounded-xl bg-white border border-[#E0E0E0]" data-testid={`coupon-${coupon.coupon_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono font-bold text-primary text-sm">{coupon.code}</span>
                    <button onClick={() => copyCode(coupon.code)} className="text-muted-foreground hover:text-primary"><Copy className="h-3 w-3" /></button>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                      {coupon.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {coupon.type === 'percentage' ? `${coupon.value}% off` : `₹${coupon.value} off`}
                    {coupon.max_discount > 0 ? ` (max ₹${coupon.max_discount})` : ''}
                    {coupon.min_order_amount > 0 ? ` | Min ₹${coupon.min_order_amount}` : ''}
                    {coupon.expiry_date ? ` | Expires: ${new Date(coupon.expiry_date).toLocaleDateString('en-IN')}` : ''}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Used: {coupon.used_count || 0}{coupon.usage_limit > 0 ? `/${coupon.usage_limit}` : ''} | Per user: {coupon.per_user_limit || 'unlimited'}
                  </p>
                </div>
                <div className="flex gap-1 items-start">
                  <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => handleEdit(coupon)} data-testid={`edit-coupon-${coupon.coupon_id}`}><Pencil className="h-3 w-3" /></Button>
                  <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => handleDelete(coupon.coupon_id)} data-testid={`delete-coupon-${coupon.coupon_id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoupons;
